import type { Ecriture } from '@shared/types/Ecriture';
import { EtatsFinanciersService } from '@domain/comptabilite/EtatsFinanciersService';
import {
  IS_BANQUES_MINES,
  IS_DROIT_COMMUN,
  IS_MINES,
  IS_PETROLE,
} from '@shared/constants/Taux';
import { roundFCFA } from '@shared/utils/round';

/**
 * `banques_mines` : valeur historique conservée (30 %, droit commun, vaut pour les banques).
 * `minier` : 35 % (CGI art. 16 : « pétrolier et minier »).
 */
export type RegimeIS = 'droit_commun' | 'banques_mines' | 'petrole' | 'minier';

export interface LiasseMD2D {
  formulaire: 'MD2D';
  exercice: string;
  regime: RegimeIS;
  taux_is: number;
  chiffre_affaires: number;
  resultat_avant_impot: number;
  is_calcule: number;
  is_minimum: number;
  is_du: number;
  reintegrations: number;
  deductions: number;
  resultat_fiscal: number;
}

export interface LiasseISP {
  formulaire: 'ISP';
  exercice: string;
  /** 1er acompte : un quart de l'IS de l'année précédente, au plus tard le 30 novembre. */
  acompte_1: number;
  /** 2e acompte : un tiers de l'IS de l'année précédente, au plus tard le 30 janvier suivant. */
  acompte_2: number;
  total_acomptes: number;
  /** IS de référence (année précédente) moins les deux acomptes. */
  solde: number;
}

export interface LiasseISA {
  formulaire: 'ISA';
  exercice: string;
  is_du_exercice_n: number;
  acomptes_verses_n: number;
  solde_a_payer: number;
  excedent_a_reporter: number;
}

/**
 * Liasse fiscale CGI Gabon (MD2D / ISP / ISA).
 * IS droit commun 30 % (dont banques), pétrole 35 %, mines 35 % (CGI art. 16).
 * IS dû = max(résultat fiscal × taux, 1 % du CA, minimum de perception 500 000 FCFA) (CGI 2025 art. 24).
 */
export class LiasseFiscaleService {
  static readonly TAUX_IS_NORMAL = IS_DROIT_COMMUN;
  static readonly TAUX_IS_MINIMUM = 0.01;
  /**
   * Minimum de perception : 500 000 FCFA (CGI 2025 art. 24, LF 2024 et LF 2025). Le 1 000 000 de la page DGI
   * « impôt sur les sociétés » est le texte du CGI 2022, antérieur : non retenu (règle de la source la plus récente).
   */
  static readonly IS_MINIMUM_PLANCHER = 500_000;
  /** 1er acompte d'IS : un quart de l'IS de l'année précédente (CGI 2025 art. 21). */
  static readonly ACOMPTE_1_QUOTITE = 1 / 4;
  /** 2e acompte d'IS : un tiers de l'IS de l'année précédente (CGI 2025 art. 21). */
  static readonly ACOMPTE_2_QUOTITE = 1 / 3;

  static tauxForRegime(regime: RegimeIS = 'droit_commun'): number {
    switch (regime) {
      case 'petrole':
        return IS_PETROLE;
      case 'minier':
        return IS_MINES;
      case 'banques_mines':
        return IS_BANQUES_MINES;
      default:
        return IS_DROIT_COMMUN;
    }
  }

  /**
   * MD2D : calcul de l'IS annuel.
   * IS = max(résultat fiscal × taux régime, 1 % du CA, minimum de perception 500 000 FCFA).
   */
  static genererMD2D(
    ecritures: readonly Ecriture[],
    exercice: string,
    reintegrations: number = 0,
    deductions: number = 0,
    regime: RegimeIS = 'droit_commun',
  ): LiasseMD2D {
    const cr = EtatsFinanciersService.calculerCompteResultat(ecritures);
    const ca = roundFCFA(cr.produits_exploitation);
    const rai = roundFCFA(
      cr.resultat_exploitation + cr.resultat_financier + cr.produits_HAO - cr.charges_HAO,
    );
    const resultat_fiscal = roundFCFA(rai + reintegrations - deductions);
    const taux_is = this.tauxForRegime(regime);
    const is_calcule = roundFCFA(Math.max(0, resultat_fiscal) * taux_is);
    const is_minimum_ca = roundFCFA(ca * this.TAUX_IS_MINIMUM);
    const is_minimum = Math.max(is_minimum_ca, this.IS_MINIMUM_PLANCHER);
    const is_du = Math.max(is_calcule, is_minimum);

    return {
      formulaire: 'MD2D',
      exercice,
      regime,
      taux_is,
      chiffre_affaires: ca,
      resultat_avant_impot: rai,
      reintegrations,
      deductions,
      resultat_fiscal,
      is_calcule,
      is_minimum,
      is_du,
    };
  }

  /**
   * ISP : deux acomptes (CGI 2025 art. 21) : le premier égal au quart, le second au tiers de l'IS payé l'année
   * précédente, versés sans avis le 30 novembre et le 30 janvier ; le solde est payé au dépôt de la déclaration,
   * le 30 avril au plus tard.
   */
  static genererISP(exercice: string, is_n_moins_1: number): LiasseISP {
    const reference = Math.max(0, is_n_moins_1);
    const acompte_1 = roundFCFA(reference * this.ACOMPTE_1_QUOTITE);
    const acompte_2 = roundFCFA(reference * this.ACOMPTE_2_QUOTITE);
    const total = roundFCFA(acompte_1 + acompte_2);
    return {
      formulaire: 'ISP',
      exercice,
      acompte_1,
      acompte_2,
      total_acomptes: total,
      solde: roundFCFA(reference - total),
    };
  }

  /** ISA : régularisation = IS de l'exercice - acomptes versés. */
  static genererISA(exercice: string, is_du: number, acomptes_verses: number): LiasseISA {
    const solde = roundFCFA(is_du - acomptes_verses);
    return {
      formulaire: 'ISA',
      exercice,
      is_du_exercice_n: is_du,
      acomptes_verses_n: acomptes_verses,
      solde_a_payer: solde > 0 ? solde : 0,
      excedent_a_reporter: solde < 0 ? -solde : 0,
    };
  }
}
